import { useState, useEffect, useRef } from "react";
import { Trash2, ExternalLink, Mail, TrendingUp, Layers, Target, ChevronDown, Plus, Download, Eye, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../../lib/api";
import StudioResult from "./StudioResult";
import FitInsights from "./FitInsights";
import FitTips from "./FitTips";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";

const STATUSES = ["tailored", "applied", "interview", "offer", "rejected"];
const STATUS_STYLE = {
  tailored: "text-body bg-subtle border-line-strong",
  applied: "text-info bg-info-soft border-info/30",
  interview: "text-brand bg-brand-soft border-brand/30",
  offer: "text-success bg-success-soft border-success/30",
  rejected: "text-danger bg-danger-soft border-danger/30",
};

const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const LABEL_KEYS = ["industry", "seniority", "function"];

export default function Tracker({ applications, stats, onChange, onNew, onOpen }) {
  const [openApp, setOpenAppState] = useState(null);
  const setOpenApp = (a) => (a && onOpen ? onOpen(a.id) : setOpenAppState(a));
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ industry: "", seniority: "", function: "" });
  const labelAttempted = useRef(false);

  useEffect(() => {
    const missing = applications.some((a) => a.processing_status === "done" && !a.labels);
    if (!missing || labelAttempted.current) return;
    labelAttempted.current = true;
    api.post("/applications/label-missing")
      .then(({ data }) => { if (data.labelled) onChange?.(); })
      .catch(() => {});
  }, [applications, onChange]);

  const options = Object.fromEntries(LABEL_KEYS.map((k) => [k,
    [...new Set(applications.map((a) => a.labels?.[k]).filter(Boolean))].sort()]));
  const visible = applications.filter((a) =>
    LABEL_KEYS.every((k) => !filters[k] || a.labels?.[k] === filters[k]));

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      onChange?.();
    } catch {
      toast.error("Update failed");
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      toast.success("Application removed");
      onChange?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      await downloadFile("/applications/export", "my-applications.zip");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Tailor an application first" : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (openApp) {
    return (
      <div className="af-fade-up">
        <button
          data-testid="back-to-tracker-button"
          onClick={() => setOpenApp(null)}
          className="flex items-center gap-2 mb-5 px-4 py-2 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to applications
        </button>
        <div className="mb-6">
          <div className="font-mono2 text-xs uppercase tracking-widest text-brand mb-1">Saved application</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{openApp.role} · {openApp.company}</h1>
        </div>
        <StudioResult app={openApp} onEmailed={onChange} onUpdated={onChange} />
      </div>
    );
  }

  return (
    <div className="af-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Applications</h1>
          <p className="text-body mt-1">Track every application, from first draft to offer.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="export-all-button"
            onClick={exportAll}
            disabled={exporting || applications.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-line text-sm text-ink hover:bg-subtle transition-colors duration-200 disabled:opacity-40"
          >
            <Download className="w-4 h-4" /> {exporting ? "Zipping…" : "Download all"}
          </button>
          <button
            data-testid="add-new-app-button"
            onClick={onNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand text-onbrand text-sm font-semibold hover:bg-brand-hover transition-colors duration-200"
          >
            <Plus className="w-4 h-4" /> New application
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <Stat icon={Layers} label="Total tailored" value={stats?.total_applications ?? 0} />
        <Stat icon={Target} label="This month" value={stats?.applications_this_month ?? 0} />
        <Stat icon={TrendingUp} label="Avg ATS score" value={`${stats?.avg_ats_score ?? 0}%`} accent />
        <Stat icon={Mail} label="Interviews this month" value={`${stats?.interviews_this_month ?? 0}`} accent />
      </div>

      <FitInsights applications={applications} />
      <FitTips labelledCount={applications.filter((a) => a.labels && a.processing_status === "done").length} />

      {applications.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface p-12 text-center">
          <p className="text-body">No applications yet. Tailor your first CV to get started.</p>
        </div>
      ) : (
        <>
        {LABEL_KEYS.some((k) => options[k].length > 0) && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="tracker-filters">
            <span className="text-xs font-mono2 uppercase tracking-widest text-faint mr-1">Filter</span>
            {LABEL_KEYS.map((k) => (
              <Select key={k} value={filters[k] || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, [k]: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid={`tracker-filter-${k}`} className="w-auto min-w-[150px] h-9 rounded-full bg-surface border-line text-sm text-ink">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-line text-ink">
                  <SelectItem value="all" data-testid={`tracker-filter-${k}-all`}>All {k === "function" ? "functions" : k === "industry" ? "industries" : "levels"}</SelectItem>
                  {options[k].map((v) => <SelectItem key={v} value={v} data-testid={`tracker-filter-${k}-${v}`}>{pretty(v)}</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
            {LABEL_KEYS.some((k) => filters[k]) && (
              <button data-testid="tracker-filter-clear" onClick={() => setFilters({ industry: "", seniority: "", function: "" })} className="text-xs text-brand hover:underline ml-1">
                Clear
              </button>
            )}
            <span className="text-xs text-faint ml-auto" data-testid="tracker-filter-count">{visible.length} of {applications.length}</span>
          </div>
        )}
        <div className="rounded-xl border border-line overflow-hidden" data-testid="application-table">
          <div className="hidden md:grid grid-cols-[1fr_100px_150px_120px_90px] gap-3 px-5 py-3 bg-surface border-b border-line text-xs font-mono2 uppercase tracking-widest text-faint">
            <span>Role / Company</span><span>ATS</span><span>Status</span><span>Date</span><span></span>
          </div>
          {visible.length === 0 && (
            <div className="px-5 py-10 text-center bg-canvas" data-testid="tracker-filter-empty">
              <p className="text-body text-sm">No applications match these filters.</p>
              <button onClick={() => setFilters({ industry: "", seniority: "", function: "" })} className="mt-2 text-sm text-brand hover:underline" data-testid="tracker-filter-empty-clear">
                Clear filters
              </button>
            </div>
          )}
          {visible.map((a) => (
            <div key={a.id} data-testid={`app-row-${a.id}`} className="px-4 md:px-5 py-4 border-b border-line bg-surface hover:bg-subtle transition-colors duration-200 md:grid md:grid-cols-[1fr_100px_150px_120px_90px] md:gap-3 md:items-center">
              <div className="flex items-start justify-between gap-3 md:contents">
                <div
                  className={`min-w-0 ${a.processing_status === "done" ? "cursor-pointer group" : ""}`}
                  data-testid={`open-row-${a.id}`}
                  onClick={() => a.processing_status === "done" && setOpenApp(a)}
                >
                  <div className="font-medium truncate flex items-center gap-2 group-hover:text-brand transition-colors">
                    {a.role || "Untitled role"}
                    {a.jd_url && <a href={a.jd_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-faint hover:text-brand"><ExternalLink className="w-3.5 h-3.5" /></a>}
                  </div>
                  <div className="text-sm text-faint truncate">{a.company || "—"}{a.email_sent && <span className="text-brand"> · emailed</span>}</div>
                  {a.labels && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`app-labels-${a.id}`}>
                      {LABEL_KEYS.map((k) => a.labels[k] && (
                        <span key={k} className="px-2 py-0.5 rounded-full border border-line text-xs text-body">{pretty(a.labels[k])}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  {a.processing_status === "processing" ? (
                    <span className="font-mono2 text-xs text-brand animate-pulse">tailoring…</span>
                  ) : (
                    <span className={`font-mono2 text-sm font-bold ${a.ats_score >= 80 ? "text-brand" : a.ats_score >= 60 ? "text-warning" : "text-danger"}`}>{a.ats_score}%</span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-3 md:mt-0 md:contents">
                <div className="relative w-fit">
                  <select
                    data-testid={`status-select-${a.id}`}
                    value={a.status}
                    onChange={(e) => changeStatus(a.id, e.target.value)}
                    className={`appearance-none cursor-pointer text-xs font-mono2 uppercase tracking-wide pl-3 pr-7 py-1.5 rounded-full border ${STATUS_STYLE[a.status]} focus:outline-none`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-surface text-ink">{s}</option>)}
                  </select>
                  <ChevronDown className="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-faint" />
                </div>
                <div className="text-sm text-faint font-mono2">{(a.created_at || "").slice(0, 10)}</div>
                <div className="flex items-center gap-1.5 justify-end">
                  <button data-testid={`open-app-${a.id}`} title="Open / edit" disabled={a.processing_status !== "done"} onClick={() => setOpenApp(a)} className="p-2 rounded-lg text-body hover:text-brand hover:bg-subtle transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button title="Download CV" disabled={a.processing_status !== "done"} onClick={() => downloadFile(`/applications/${a.id}/cv.pdf`, `CV_${a.company || "app"}.pdf`).catch(() => toast.error("Tailor this application first"))} className="p-2 rounded-lg text-body hover:text-brand hover:bg-subtle transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Download className="w-4 h-4" />
                  </button>
                  <button data-testid={`delete-app-${a.id}`} title="Delete" onClick={() => remove(a.id)} className="p-2 rounded-lg text-body hover:text-danger hover:bg-subtle transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <div className="flex items-center gap-2 text-faint mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-mono2 uppercase tracking-widest">{label}</span>
      </div>
      <div className={`font-display text-2xl font-extrabold ${accent ? "text-brand" : "text-ink"}`}>{value}</div>
    </div>
  );
}
